import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/lib/modules";
import { Card } from "@/components/ui/card";
import {
  ShoppingCart, Package, Users, PieChart, Wallet, BellRing, Library, Settings,
  ArrowRight, FileText, Receipt, BarChart3, ClipboardList, PackageCheck, ScrollText, LifeBuoy,
} from "lucide-react";

// Presentation metadata only. `module` = registry module that gates the tile (same rule as sidebar nav: enabled OR history);
// core surfaces have no module gate. `roles` MUST NOT be more permissive than the matching Layout.js NAV entry / App.js route
// (Build B alignment rule: AR Aging owner+manager, Inventory owner+manager; Payments has no role restriction in NAV either).
export const MODULE_CARDS = [
  { module: null, to: "/pos", label: "POS Register", icon: ShoppingCart, color: "emerald" },
  { module: "quotations", to: "/quotations", label: "Quotations", icon: FileText, color: "sky" },
  { module: null, to: "/inventory", label: "Inventory", icon: Package, color: "sky", roles: ["owner", "manager"] },
  { module: null, to: "/customers", label: "Customers", icon: Users, color: "violet" },
  { module: "customers_credit", to: "/ar-aging", label: "AR Aging", icon: PieChart, color: "rose", roles: ["owner", "manager"] },
  { module: "customers_credit", to: "/payments", label: "Payments", icon: Wallet, color: "amber" },
  { module: "expenses", to: "/expenses", label: "Expenses", icon: Receipt, color: "rose", roles: ["owner", "manager"] },
  { module: null, to: "/reports", label: "Reports", icon: BarChart3, color: "violet", roles: ["owner", "manager"] },
  { module: "reminders", to: "/reminders", label: "Reminders", icon: BellRing, color: "cyan", roles: ["owner", "manager"] },
  { module: "hardware_library", to: "/library", label: "Hardware Library", icon: Library, color: "orange", roles: ["owner", "manager"] },
  { module: null, to: "/settings", label: "Settings", icon: Settings, color: "slate", roles: ["owner", "manager"] },
];

// Staff quick actions: every target is an un-roled NAV entry / route (available to all roles).
export const QUICK_ACTIONS = [
  { module: null, to: "/pos", label: "POS Register", icon: ShoppingCart, color: "emerald" },
  { module: null, to: "/shift", label: "Shift Report", icon: ClipboardList, color: "amber" },
  { module: null, to: "/pickups", label: "Pickups", icon: PackageCheck, color: "sky" },
  { module: null, to: "/sales-history", label: "Sales History", icon: ScrollText, color: "violet" },
  { module: null, to: "/customers", label: "Customers", icon: Users, color: "violet" },
  { module: "quotations", to: "/quotations", label: "Quotations", icon: FileText, color: "sky" },
  { module: null, to: "/help", label: "Help & Support", icon: LifeBuoy, color: "slate" },
];

const colorMap = {
  emerald: "bg-emerald-500/10 text-emerald-500 group-hover:bg-emerald-500",
  sky: "bg-sky-500/10 text-sky-500 group-hover:bg-sky-500",
  violet: "bg-violet-500/10 text-violet-500 group-hover:bg-violet-500",
  rose: "bg-rose-500/10 text-rose-500 group-hover:bg-rose-500",
  amber: "bg-amber-500/10 text-amber-500 group-hover:bg-amber-500",
  cyan: "bg-cyan-500/10 text-cyan-500 group-hover:bg-cyan-500",
  orange: "bg-orange-500/10 text-orange-500 group-hover:bg-orange-500",
  slate: "bg-slate-500/10 text-slate-500 group-hover:bg-slate-500",
};

export function TileGrid({ title, items, testid }) {
  const { user, business } = useAuth();
  const modules = useModules();
  const nav = useNavigate();
  const visible = items.filter((m) => (!m.module || modules.isAvailable(m.module)) && (!m.roles || m.roles.includes(user?.role))
    && !(m.to === "/reports" && user?.role === "manager" && business?.managers_can_access_reports === false));
  return (
    <div data-testid={testid}>
      <h2 className="font-head text-xl font-bold mb-4">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {visible.map((m) => {
          const Icon = m.icon;
          return (
            <button key={m.to} onClick={() => nav(m.to)} data-testid={`module-${m.label.toLowerCase().replace(/[\s&]+/g, "-")}`} className="group text-left">
              <Card className="p-5 h-full hover:border-emerald-500/40 hover:shadow-xl transition-all hover:-translate-y-1">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors group-hover:text-white ${colorMap[m.color]}`}>
                  <Icon className="w-6 h-6" />
                </div>
                <p className="font-head font-bold mt-4 flex items-center justify-between">{m.label}
                  <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" /></p>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}
